import React, {
    useEffect,
    useState,
    useRef,
    useLayoutEffect,
    useCallback
} from 'react';
import { Button, Icon, Tooltip } from '@ferris/react';
import PropTypes from 'prop-types';
import cn from 'classnames';

const DESCENDER_PADDING_PX = 4;

function HeroV2(props) {
    const {
        titleList,
        titleSuffix,
        copy,
        accessibilityButton,
        display,
        okcOfferLink
    } = props;

    if (!display) return null;

    const { play: playText, pause: pauseText } = accessibilityButton || {};

    const [pause, setPause] = useState(false);
    const [itemHeight, setItemHeight] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const listRef = useRef(null);
    const resizeHandleRef = useRef(null);

    const normalizedTitleList = Array.isArray(titleList) ? titleList : [];
    const titleCount = normalizedTitleList.length;

    const measureItemHeight = useCallback(() => {
        if (!listRef.current) return;
        const nodes = Array.from(listRef.current.children || []);
        if (nodes.length === 0) return;
        const heights = nodes.map((n) => n.offsetHeight || 0);
        const max = Math.max(...heights);
        if (max > 0) {
            if (!isReady) {
                setIsReady(true);
            }
            if (max !== itemHeight) {
                setItemHeight(max);
            }
        }
    }, [itemHeight, isReady]);

    const toggleAnimation = useCallback(() => {
        setPause((prevPause) => !prevPause);
    }, []);

    // JavaScript-based animation instead of CSS
    useEffect(() => {
        if (pause || titleCount <= 1) {
            return undefined;
        }

        const interval = setInterval(() => {
            setIsTransitioning(false);
            setCurrentIndex((prev) => {
                const nextIndex = prev + 1;
                // If we're about to go past the last real item, go to the duplicate first item
                if (nextIndex >= titleCount) {
                    return titleCount; // This shows the duplicate first item
                }
                return nextIndex;
            });
        }, 5000); // 5 seconds per item (slower)

        return () => {
            clearInterval(interval);
        };
    }, [pause, titleCount]);

    useLayoutEffect(() => {
        measureItemHeight();
    }, [measureItemHeight, titleList]);

    useEffect(() => {
        if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
            document.fonts.ready
                .then(() => {
                    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                        window.requestAnimationFrame(() => {
                            measureItemHeight();
                        });
                    } else {
                        measureItemHeight();
                    }
                })
                .catch((error) => {
                    if (process.env.NODE_ENV !== 'production') {
                        // eslint-disable-next-line no-console
                        console.warn('HeroV2: Font loading failed, text measurements may be inaccurate:', error);
                    }
                    setTimeout(() => {
                        measureItemHeight();
                    }, 500);
                });
        }
    }, [measureItemHeight]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleResize = () => {
            if (resizeHandleRef.current) {
                return;
            }

            if (typeof window.requestAnimationFrame === 'function') {
                const id = window.requestAnimationFrame(() => {
                    resizeHandleRef.current = null;
                    measureItemHeight();
                });
                resizeHandleRef.current = {
                    type: 'raf',
                    id
                };
            } else {
                const timeoutId = setTimeout(() => {
                    resizeHandleRef.current = null;
                    measureItemHeight();
                }, 60);
                resizeHandleRef.current = {
                    type: 'timeout',
                    id: timeoutId
                };
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            if (resizeHandleRef.current) {
                if (resizeHandleRef.current.type === 'raf' && typeof window.cancelAnimationFrame === 'function') {
                    window.cancelAnimationFrame(resizeHandleRef.current.id);
                } else if (resizeHandleRef.current.type === 'timeout') {
                    clearTimeout(resizeHandleRef.current.id);
                }
                resizeHandleRef.current = null;
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [measureItemHeight]);

    // Calculate the transform based on current index and item height
    const transform = itemHeight ? `translateY(-${currentIndex * itemHeight}px)` : 'translateY(0px)';

    const listStyle = {
        height: itemHeight ? `${itemHeight}px` : 'auto',
        overflow: 'hidden',
        position: 'relative'
    };

    const handleTransitionEnd = useCallback(() => {
        // If we just finished transitioning to the duplicate first item (at the end)
        if (currentIndex === titleCount) {
            setIsTransitioning(true);
            requestAnimationFrame(() => {
                setCurrentIndex(0);
            });
        }
    }, [currentIndex, titleCount]);

    useEffect(() => {
        if (isTransitioning && currentIndex === 0) {
            requestAnimationFrame(() => {
                setIsTransitioning(false);
            });
        }
    }, [currentIndex, isTransitioning]);

    const getTransition = () => {
        if (isTransitioning || pause) return 'none';
        return 'transform 1.2s ease-in-out';
    };

    const ulStyle = {
        transform,
        transition: getTransition(),
        margin: 0,
        padding: 0,
        listStyle: 'none',
        visibility: isReady ? 'visible' : 'hidden'
    };

    const hasTitles = titleCount > 0;
    const firstTitle = hasTitles ? normalizedTitleList[0] : '';

    const listItemStyle = {
        paddingBottom: `${DESCENDER_PADDING_PX}px`
    };

    return (
        <div className={cn('ulx__aem-section', 'ulx__aem-hero__content')}>
            <h1>
                <div
                    className={cn('rotating-list')}
                    style={listStyle}
                    role="marquee"
                >
                    {
                        !isReady && hasTitles && (
                            <span
                                className={cn('rotating-list__placeholder')}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingBottom: `${DESCENDER_PADDING_PX}px`
                                }}
                            >
                                {firstTitle}
                            </span>
                        )
                    }
                    <ul ref={listRef} style={ulStyle} onTransitionEnd={handleTransitionEnd}>
                        {
                            // eslint-disable-next-line react/no-array-index-key
                            normalizedTitleList.map((title, i) => (
                                <li key={`title-item-${i}`} style={listItemStyle}>{title}</li> /* eslint-disable-line react/no-array-index-key */
                            ))
                        }
                        {hasTitles && (
                            <li style={listItemStyle}>{normalizedTitleList[0]}</li>
                        )}
                    </ul>
                </div>
                <div className={cn('ulx__aem-hero__title-suffix')}>
                    <span>
                        {titleSuffix}
                    </span>
                    {
                        accessibilityButton
                        && (
                            <Tooltip
                                classes={cn('button-play-pause-tooltip')}
                                position="right"
                                text={pause ? playText : pauseText}
                            >
                                <Button
                                    classes={cn('button-play-pause')}
                                    onClick={toggleAnimation}
                                    htmlButtonType="button"
                                    title={pause ? playText : pauseText}
                                    type="round"
                                    variant="round-reversed"
                                    small
                                >
                                    <Icon name={pause ? 'play' : 'pause'} />
                                </Button>
                            </Tooltip>
                        )
                    }
                </div>
            </h1>
            <p>{copy}</p>
            {okcOfferLink && (
                <div className={cn('ulx__aem-hero__okc-offer-link')}>
                    <a
                        href="#Testimonials"
                        className={cn('ulx__aem-hero__offer-details-link')}
                    >
                        {okcOfferLink}
                    </a>
                </div>
            )}
        </div>
    );
}

HeroV2.propTypes = {
    copy: PropTypes.string.isRequired,
    display: PropTypes.bool.isRequired,
    titleList: PropTypes.arrayOf(PropTypes.string),
    titleSuffix: PropTypes.string.isRequired,
    accessibilityButton: PropTypes.shape({
        play: PropTypes.string.isRequired,
        pause: PropTypes.string.isRequired
    }),
    okcOfferLink: PropTypes.string
};

HeroV2.defaultProps = {
    accessibilityButton: null,
    okcOfferLink: null,
    titleList: []
};

export default HeroV2;
